import {
  createContentfulAssetUrl,
  createContentfulEntryUrl,
} from "app/helpers/contentfulHelper";
import { LanguageSelectionHelper } from "app/helpers/languageSelectionHelper";
import { landingPage, ResponsiveScreenSizesInt } from "app/utils/const";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import ENV from "../../../../../config";
import LandingPageImage from "./components/LandingPageImage";
import "./LandingPagePromo.scss";

interface PromoContent {
  buttonUrl: string;
  image1: { url: string; alt: string };
  mobileImageOne: { url: string; alt: string };
  image1Title: string;
  image1Text: string;
  image1Button: string;
  image2: { url: string; alt: string };
  mobileImageTwo: { url: string; alt: string };
  image2Title: string;
  image2Text: string;
  image2Button: string;
}

const LandingPagePromo = () => {
  const [content, setContent] = useState<PromoContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [locale, setLocale] = useState("en-US");

  const spaceId = landingPage.CONTENTFUL_SPACE_ID;
  const accessToken = landingPage.CONTENTFUL_ACCESS_TOKEN;
  const environment = landingPage.CONTENTFUL_ENVIRONMENT_MASTER;

  // Verbose environment handling
  const currentDomain = window.location.hostname;
  const isPreProd = currentDomain.includes("3etpp.com");
  const isProd = ENV.PRODUCTION === "TRUE" || isPreProd;

  // Entry IDs for different environments
  const prodEntryId = "4xlsHO7wdAALbCAxeBRSVi";
  const stagingEntryId = "2zf3lO9vcKjZDYz50RHLQH";

  // Final entry ID based on environment
  const entryId =
    ENV.PRODUCTION === "TRUE" || isPreProd
      ? prodEntryId
      : ENV.PRODUCTION === "FALSE" && !isPreProd
      ? stagingEntryId
      : stagingEntryId;
  const buttonUrl =
    isProd || isPreProd
      ? "https://sports.3et.com/register/"
      : "https://www.sports-alpha.3et.com/register/";

  const seeMoreUrl =
    isProd || isPreProd
      ? "https://3et.com/promotions/signup"
      : "https://www.sports-alpha.3et.com/promotions/signup#terms-and-conditions";

  useEffect(() => {
    if (LanguageSelectionHelper.checkSelectedLanguage().includes("es")) {
      setLocale("es");
    }
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const entryUrl = createContentfulEntryUrl({
          spaceId,
          environment,
          accessToken,
          entryId,
          locale,
        });

        const response = await fetch(entryUrl);
        const data = await response.json();

        const fetchImage = async (imageField: any) => {
          if (!imageField?.sys?.id) return null;
          const assetUrl = createContentfulAssetUrl({
            spaceId,
            accessToken,
            assetId: imageField.sys.id,
          });
          const imgResponse = await fetch(assetUrl);
          const imgData = await imgResponse.json();
          return {
            url: `https:${imgData.fields.file.url}`,
            alt: imgData.fields.title || "Promo Image",
          };
        };

        const image1 = await fetchImage(data.fields.desktopImage);
        const mobileImageOne =
          (await fetchImage(data.fields.mobileImage)) || image1;

        setContent({
          buttonUrl,
          image1: image1 || { url: "", alt: "Promo Image" },
          mobileImageOne: mobileImageOne || { url: "", alt: "Promo Image" },
          image1Title: data.fields.desktopImageTitle || "",
          image1Text: data.fields.desktopImageText || "",
          image1Button: data.fields.desktopImageButton || "",
          image2: { url: "", alt: "" },
          mobileImageTwo: { url: "", alt: "" },
          image2Title: "",
          image2Text: "",
          image2Button: "",
        });

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    }

    fetchData();
  }, [locale, entryId, buttonUrl, spaceId, environment, accessToken]);

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= ResponsiveScreenSizesInt.mobile
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= ResponsiveScreenSizesInt.mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading)
    return (
      <p className="loading-text">
        <FormattedMessage tagName="span" id="competition-expanded-loading" />
      </p>
    );

  if (!content)
    return (
      <p className="error-text">
        <FormattedMessage tagName="span" id="content-not-found" />
      </p>
    );

  const { image1, mobileImageOne, image1Title, image1Text, image1Button } =
    content;

  return (
    <div className="landing-page-container">
      <div className="landing-page-image-wrapper">
        <LandingPageImage
          desktopImage={image1}
          mobileImage={mobileImageOne}
          className="landing-page-image"
        />
        <div className="landing-page-overlay">
          <h2 className="landing-page-title">{image1Title}</h2>
          <p className="landing-page-text">{image1Text}</p>

          <div className="landing-page-actions">
            {image1Button && (
              <a href={buttonUrl} className="landing-page-button">
                {image1Button}
              </a>
            )}
            <a
              href={seeMoreUrl}
              className="landing-page-see-more"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage
                id="landing-page-see-more"
                defaultMessage="See More"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPagePromo;
